import { Box, CircularProgress, Stack, Typography } from "@mui/material";

interface SnapshotProcessingProps {
  /** Optional override for the headline (default is customer-friendly). */
  title?: string;
  /** Optional override for the subtext. */
  subtext?: string;
  /**
   * When true, polling has hit its hard time limit. We stop the spinner and
   * show a static "check back" message instead of implying it's still
   * actively refreshing.
   */
  timedOut?: boolean;
}

const TIMED_OUT_TITLE = "Still finishing up";
const TIMED_OUT_SUBTEXT =
  "This is taking longer than usual. Please refresh the page in a few minutes.";

/**
 * Interstitial shown while a Tier II report's snapshot is being created
 * asynchronously (the GraphQL `tierIIReport` query is throwing
 * `SNAPSHOT_PENDING`). The page renders this until the report query stops
 * returning that code; callers wire up polling on the query separately.
 */
export const SnapshotProcessing = ({
  title = "Finishing up your report",
  subtext = "This usually takes less than a minute. The page will refresh automatically.",
  timedOut = false,
}: SnapshotProcessingProps) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flex={1}
      sx={{ minHeight: "60vh", px: 3 }}
    >
      <Stack spacing={3} alignItems="center" maxWidth={480} textAlign="center">
        {!timedOut && <CircularProgress />}
        <Typography variant="h5" component="h1" sx={{ fontWeight: 300 }}>
          {timedOut ? TIMED_OUT_TITLE : title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {timedOut ? TIMED_OUT_SUBTEXT : subtext}
        </Typography>
      </Stack>
    </Box>
  );
};
